import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function PartnersPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Partners' },
    { id: 'investment', label: 'Investment & Financial' },
    { id: 'legal', label: 'Legal & Governance' },
    { id: 'luxury', label: 'Luxury & Lifestyle' },
    { id: 'knowledge', label: 'Knowledge & Media' },
  ];

  const partners = [
    {
      name: 'Global Private Equity Group',
      category: 'investment',
      tier: 'platinum',
      description: 'Leading cross-border private equity investments',
    },
    {
      name: 'Cross Border Capital Advisors',
      category: 'investment',
      tier: 'gold',
      description: 'Strategic capital allocation and advisory',
    },
    {
      name: 'Sovereign Wealth Strategies',
      category: 'investment',
      tier: 'platinum',
      description: 'Institutional wealth management excellence',
    },
    {
      name: 'Alternative Assets Management',
      category: 'investment',
      tier: 'gold',
      description: 'Real assets and alternative investments',
    },
    {
      name: 'International Family Office Law',
      category: 'legal',
      tier: 'platinum',
      description: 'Multi-jurisdictional family office structuring',
    },
    {
      name: 'Governance & Succession Advisors',
      category: 'legal',
      tier: 'gold',
      description: 'Next-gen governance frameworks',
    },
    {
      name: 'Cross Border Legal Solutions',
      category: 'legal',
      tier: 'gold',
      description: 'International wealth structuring',
    },
    {
      name: 'Trust & Estate Planning Group',
      category: 'legal',
      tier: 'platinum',
      description: 'Legacy and estate architecture',
    },
    {
      name: 'Five Star Beirut Collection',
      category: 'luxury',
      tier: 'platinum',
      description: 'Exceptional hospitality experiences',
    },
    {
      name: 'Luxury Concierge Services',
      category: 'luxury',
      tier: 'gold',
      description: 'Bespoke lifestyle management',
    },
    {
      name: 'Private Aviation Partners',
      category: 'luxury',
      tier: 'platinum',
      description: 'Seamless global travel solutions',
    },
    {
      name: 'Exclusive Experiences Curator',
      category: 'luxury',
      tier: 'gold',
      description: 'Curated cultural immersions',
    },
    {
      name: 'Global Wealth Insights',
      category: 'knowledge',
      tier: 'gold',
      description: 'Research and market intelligence',
    },
    {
      name: 'Family Office Magazine',
      category: 'knowledge',
      tier: 'platinum',
      description: 'Leading industry publication',
    },
    {
      name: 'Executive Leadership Forum',
      category: 'knowledge',
      tier: 'gold',
      description: 'Thought leadership platform',
    },
    {
      name: 'Women in Finance Network',
      category: 'knowledge',
      tier: 'platinum',
      description: 'Global community and advocacy',
    },
  ];

  const filteredPartners =
    activeCategory === 'all'
      ? partners
      : partners.filter((p) => p.category === activeCategory);
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Summit Partners",
    "description": "Explore partnership opportunities with the Women In Wealth Summit. Strategic alliances in capital, culture, and care.",
    "about": {
      "@type": "Event",
      "name": "Women In Wealth Summit",
      "sponsor": partners.map(partner => ({
        "@type": "Organization",
        "name": partner.name,
        "description": partner.description
      }))
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Partners | Women In Wealth Summit · Beirut 2026"
        description="Explore partnership opportunities with the Women In Wealth Summit. Strategic alliances in investment, legal, luxury, and knowledge sectors. Platinum, Gold, and Silver partnership tiers available."
        keywords="summit partners, partnership opportunities, summit sponsors, strategic partnerships, platinum partnership, gold partnership, media partners"
        canonical={`${siteUrl}/partners`}
        schema={schema}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#1A1A2E] via-[#2A2A3E] to-[#1A1A2E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E8B4B8] rounded-full blur-3xl"></div>
        </div>
        <div className="px-8 lg:px-16 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-8">
              <p className="text-xs uppercase tracking-widest text-[#D4AF37]">Strategic Alliances</p>
            </div>
            <h1 className="text-5xl lg:text-7xl font-serif font-bold text-white mb-6 leading-tight">
              Partners in Excellence
            </h1>
            <p className="text-xl text-white/80 leading-relaxed max-w-2xl mx-auto mb-10">
              Curated partnerships in capital, culture, and care — aligned with the summit's values and global vision
            </p>
            <a
              href="#partnership-tiers"
              className="inline-flex items-center gap-3 px-10 py-5 bg-[#D4AF37] text-white rounded-full font-medium text-base hover:bg-[#C49B2E] transition-all shadow-2xl hover:shadow-[#D4AF37]/50 whitespace-nowrap cursor-pointer group"
            >
              <i className="ri-handshake-line text-xl"></i>
              Become a Partner
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 bg-[#F8F6F2] sticky top-20 z-30 border-b border-gray-200">
        <div className="px-8 lg:px-16">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-[#1A1A2E] text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="py-24 bg-white">
        <div className="px-8 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPartners.map((partner, index) => (
                <div
                  key={index}
                  className="group relative bg-gradient-to-br from-[#F8F6F2] to-white p-8 rounded-2xl border border-gray-200 hover:border-[#D4AF37] transition-all duration-500 hover:shadow-2xl cursor-pointer overflow-hidden"
                >
                  {/* Tier Badge */}
                  <div className="absolute top-4 right-4">
                    <div
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        partner.tier === 'platinum'
                          ? 'bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-white'
                          : 'bg-gradient-to-r from-gray-400 to-gray-500 text-white'
                      }`}
                    >
                      {partner.tier.toUpperCase()}
                    </div>
                  </div>

                  {/* Decorative Corner */}
                  <div className="absolute top-0 left-0 w-20 h-20 bg-gradient-to-br from-[#D4AF37]/10 to-transparent rounded-br-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  {/* Content */}
                  <div className="relative z-10 pt-8">
                    <h3 className="text-xl font-serif font-bold text-gray-900 mb-3 group-hover:text-[#1A1A2E] transition-colors">
                      {partner.name}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-6">
                      {partner.description}
                    </p>

                    {/* Hover Arrow */}
                    <div className="flex items-center gap-2 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <span className="text-xs uppercase tracking-wider font-medium">Learn More</span>
                      <i className="ri-arrow-right-line text-lg"></i>
                    </div>
                  </div>

                  {/* Background Pattern */}
                  <div className="absolute bottom-0 right-0 w-32 h-32 opacity-5 group-hover:opacity-10 transition-opacity duration-500">
                    <div className="w-full h-full bg-gradient-to-tl from-[#1A1A2E] to-transparent rounded-tl-full"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Press & Media Section */}
      <section className="py-24 bg-[#1A1A2E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-80 h-80 bg-[#D4AF37] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#E8B4B8] rounded-full blur-3xl"></div>
        </div>
        <div className="px-8 lg:px-16 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
                <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-medium">Exclusive Coverage</p>
              </div>
              <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-6">
                Press & Media Partners
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
                A carefully curated selection of prestigious media partners documenting the summit with discretion and excellence
              </p>
            </div>

            {/* Media Partners Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {/* Forbes */}
              <div className="group bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500 hover:bg-white/10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl">
                    <i className="ri-newspaper-line text-2xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-bold text-white">Forbes Women</h3>
                    <p className="text-xs text-[#D4AF37] uppercase tracking-wider">Global Publication</p>
                  </div>
                </div>
                <p className="text-sm text-white/60 leading-relaxed">
                  Exclusive feature coverage highlighting the summit's most influential voices and groundbreaking discussions
                </p>
              </div>

              {/* Financial Times */}
              <div className="group bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500 hover:bg-white/10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl">
                    <i className="ri-file-text-line text-2xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-bold text-white">Financial Times</h3>
                    <p className="text-xs text-[#D4AF37] uppercase tracking-wider">Business & Finance</p>
                  </div>
                </div>
                <p className="text-sm text-white/60 leading-relaxed">
                  In-depth analysis and thought leadership pieces on wealth management and family office trends
                </p>
              </div>

              {/* Vogue Business */}
              <div className="group bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500 hover:bg-white/10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl">
                    <i className="ri-quill-pen-line text-2xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-bold text-white">Vogue Business</h3>
                    <p className="text-xs text-[#D4AF37] uppercase tracking-wider">Luxury & Lifestyle</p>
                  </div>
                </div>
                <p className="text-sm text-white/60 leading-relaxed">
                  Capturing the elegance and sophistication of the summit experience through visual storytelling
                </p>
              </div>

              {/* Bloomberg */}
              <div className="group bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500 hover:bg-white/10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl">
                    <i className="ri-line-chart-line text-2xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-bold text-white">Bloomberg Wealth</h3>
                    <p className="text-xs text-[#D4AF37] uppercase tracking-wider">Financial Media</p>
                  </div>
                </div>
                <p className="text-sm text-white/60 leading-relaxed">
                  Expert coverage of investment strategies and wealth preservation insights shared at the summit
                </p>
              </div>

              {/* Tatler */}
              <div className="group bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500 hover:bg-white/10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl">
                    <i className="ri-vip-diamond-line text-2xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-bold text-white">Tatler</h3>
                    <p className="text-xs text-[#D4AF37] uppercase tracking-wider">High Society</p>
                  </div>
                </div>
                <p className="text-sm text-white/60 leading-relaxed">
                  Exclusive profiles of distinguished attendees and behind-the-scenes glimpses of the summit
                </p>
              </div>

              {/* Arabian Business */}
              <div className="group bg-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500 hover:bg-white/10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl">
                    <i className="ri-global-line text-2xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif font-bold text-white">Arabian Business</h3>
                    <p className="text-xs text-[#D4AF37] uppercase tracking-wider">Regional Focus</p>
                  </div>
                </div>
                <p className="text-sm text-white/60 leading-relaxed">
                  Regional perspective on cross-border wealth and the growing influence of women in MENA finance
                </p>
              </div>
            </div>

            {/* Privacy Note */}
            <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#E8B4B8]/20 backdrop-blur-sm p-8 rounded-2xl border border-[#D4AF37]/30">
              <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
                <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37]/20 rounded-full flex-shrink-0">
                  <i className="ri-shield-check-line text-3xl text-[#D4AF37]"></i>
                </div>
                <div>
                  <h4 className="text-xl font-serif font-bold text-white mb-2">Privacy & Exclusivity Guaranteed</h4>
                  <p className="text-white/70 text-sm leading-relaxed">
                    Our media partners operate under strict confidentiality agreements. Coverage is strategically curated and not publicly shared, ensuring the privacy of all participants while documenting the summit's significance for posterity.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Tiers */}
      <section id="partnership-tiers" className="py-24 bg-gradient-to-br from-[#F8F6F2] to-white">
        <div className="px-8 lg:px-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-[#D4AF37]/10 rounded-full mb-6">
                <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-medium">Partnership Opportunities</p>
              </div>
              <h2 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-6">
                Partnership Tiers
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Tailored partnership opportunities designed to align with your brand and objectives
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Platinum Tier */}
              <div className="bg-gradient-to-br from-[#1A1A2E] to-[#2A2A3E] p-10 rounded-3xl text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37] rounded-full blur-3xl opacity-20"></div>
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-white text-xs font-medium rounded-full">MOST POPULAR</span>
                </div>
                <div className="relative z-10">
                  <div className="w-16 h-16 flex items-center justify-center mb-6">
                    <i className="ri-vip-crown-line text-4xl text-[#D4AF37]"></i>
                  </div>
                  <h3 className="text-3xl font-serif font-bold mb-2">Platinum</h3>
                  <p className="text-[#D4AF37] text-sm font-medium mb-4">Premier Partnership</p>
                  <p className="text-white/80 mb-8 leading-relaxed text-sm">
                    Premier visibility, exclusive access, and co-creation opportunities with the summit host
                  </p>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-[#D4AF37] text-xl mt-0.5"></i>
                      <span className="text-sm">Logo placement in all summit materials</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-[#D4AF37] text-xl mt-0.5"></i>
                      <span className="text-sm">Speaking or panel moderation opportunity</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-[#D4AF37] text-xl mt-0.5"></i>
                      <span className="text-sm">Private dinner with select participants</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-[#D4AF37] text-xl mt-0.5"></i>
                      <span className="text-sm">VIP seating at all sessions</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-[#D4AF37] text-xl mt-0.5"></i>
                      <span className="text-sm">Post-summit engagement opportunities</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-[#D4AF37] text-xl mt-0.5"></i>
                      <span className="text-sm">Exclusive media coverage</span>
                    </li>
                  </ul>
                  <a
                    href="mailto:partnerships@womeninwealth.com?subject=Platinum Partnership Inquiry"
                    className="inline-flex items-center gap-2 w-full justify-center px-6 py-4 bg-[#D4AF37] text-white rounded-full font-medium text-sm hover:bg-[#C49B2E] transition-all whitespace-nowrap cursor-pointer"
                  >
                    Inquire Now
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>

              {/* Gold Tier */}
              <div className="bg-white p-10 rounded-3xl border-2 border-gray-200 hover:border-[#D4AF37]/50 transition-all relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#E8B4B8] rounded-full blur-3xl opacity-10"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 flex items-center justify-center mb-6">
                    <i className="ri-medal-line text-4xl text-gray-400"></i>
                  </div>
                  <h3 className="text-3xl font-serif font-bold text-gray-900 mb-2">Gold</h3>
                  <p className="text-gray-500 text-sm font-medium mb-4">Strategic Partnership</p>
                  <p className="text-gray-600 mb-8 leading-relaxed text-sm">
                    Strategic visibility and meaningful engagement with summit participants
                  </p>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-gray-400 text-xl mt-0.5"></i>
                      <span className="text-sm text-gray-700">Logo placement in key materials</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-gray-400 text-xl mt-0.5"></i>
                      <span className="text-sm text-gray-700">Branded presence at select sessions</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-gray-400 text-xl mt-0.5"></i>
                      <span className="text-sm text-gray-700">Networking reception sponsorship</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-gray-400 text-xl mt-0.5"></i>
                      <span className="text-sm text-gray-700">Reserved seating at main sessions</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-gray-400 text-xl mt-0.5"></i>
                      <span className="text-sm text-gray-700">Digital visibility and recognition</span>
                    </li>
                  </ul>
                  <a
                    href="mailto:partnerships@womeninwealth.com?subject=Gold Partnership Inquiry"
                    className="inline-flex items-center gap-2 w-full justify-center px-6 py-4 bg-[#1A1A2E] text-white rounded-full font-medium text-sm hover:bg-[#2A2A3E] transition-all whitespace-nowrap cursor-pointer"
                  >
                    Inquire Now
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>

              {/* Silver Tier */}
              <div className="bg-white p-10 rounded-3xl border-2 border-gray-200 hover:border-[#D4AF37]/50 transition-all relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-gray-200 rounded-full blur-3xl opacity-20"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 flex items-center justify-center mb-6">
                    <i className="ri-award-line text-4xl text-gray-300"></i>
                  </div>
                  <h3 className="text-3xl font-serif font-bold text-gray-900 mb-2">Silver</h3>
                  <p className="text-gray-500 text-sm font-medium mb-4">Supporting Partnership</p>
                  <p className="text-gray-600 mb-8 leading-relaxed text-sm">
                    Essential visibility and access to connect with summit attendees
                  </p>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-gray-300 text-xl mt-0.5"></i>
                      <span className="text-sm text-gray-700">Logo on event website</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-gray-300 text-xl mt-0.5"></i>
                      <span className="text-sm text-gray-700">Social media recognition</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-gray-300 text-xl mt-0.5"></i>
                      <span className="text-sm text-gray-700">Complimentary event passes</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-gray-300 text-xl mt-0.5"></i>
                      <span className="text-sm text-gray-700">Networking event access</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-line text-gray-300 text-xl mt-0.5"></i>
                      <span className="text-sm text-gray-700">Post-event attendee list</span>
                    </li>
                  </ul>
                  <a
                    href="mailto:partnerships@womeninwealth.com?subject=Silver Partnership Inquiry"
                    className="inline-flex items-center gap-2 w-full justify-center px-6 py-4 border-2 border-[#1A1A2E] text-[#1A1A2E] rounded-full font-medium text-sm hover:bg-[#1A1A2E] hover:text-white transition-all whitespace-nowrap cursor-pointer"
                  >
                    Inquire Now
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Custom Partnership Note */}
            <div className="mt-12 text-center p-8 bg-[#1A1A2E]/5 rounded-2xl">
              <p className="text-gray-600 mb-4">
                Looking for a custom partnership package tailored to your specific needs?
              </p>
              <a
                href="mailto:partnerships@womeninwealth.com?subject=Custom Partnership Inquiry"
                className="inline-flex items-center gap-2 text-[#D4AF37] font-medium hover:underline cursor-pointer"
              >
                Contact us for bespoke opportunities
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#1A1A2E] via-[#2A2A3E] to-[#1A1A2E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl"></div>
        </div>
        <div className="px-8 lg:px-16 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-6">
              Become a Partner
            </h2>
            <p className="text-xl text-white/80 leading-relaxed mb-12 max-w-2xl mx-auto">
              Join us in creating an unparalleled experience for the world's most influential women in wealth
            </p>
            <a
              href="mailto:partnerships@womeninwealth.com"
              className="inline-flex items-center gap-3 px-10 py-5 bg-[#D4AF37] text-white rounded-full font-medium text-base hover:bg-[#C49B2E] transition-all shadow-2xl hover:shadow-[#D4AF37]/50 whitespace-nowrap cursor-pointer group"
            >
              Explore Partnership Opportunities
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
